import { GridColDef, GridColumnHeaderParams } from "@mui/x-data-grid";
import { Grid, Tooltip, Typography } from "@mui/material";
import { DownloadOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import dayjs from "dayjs";
import useLoginHistoryStore from "../../store/useLoginHistoryStore";
import { GetLoginHistoryList } from "../../api/hooks";
import api from "utils/api";
import LoadingButton from "@mui/lab/LoadingButton";

const LoginHistoryTable = () => {
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const { postModel, handleSorting, handlePagination } = useLoginHistoryStore();
  const { data, isFetching } = GetLoginHistoryList(postModel);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "memberName",
        headerName: "Member Name",
        filterable: false,
        flex: 1.5,
      },
      {
        field: "email",
        headerName: "Email",
        filterable: false,
        flex: 1.5,
      },
      {
        field: "enrollmentNo",
        headerName: "Enrollment No",
        filterable: false,
        flex: 1,
      },
      {
        field: "programName",
        headerName: "Program Name",
        filterable: false,
        flex: 2,
      },
      {
        field: "lastLoggedIn",
        headerName: "Last Logged In",
        flex: 1.2,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          return (
            <>
              <Typography variant="body1">
                {params.row.lastLoggedIn
                  ? dayjs(params.row.lastLoggedIn).format("DD-MM-YYYY")
                  : "-"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {params.row.lastLoggedIn
                  ? dayjs(params.row.lastLoggedIn).format("hh:mm A")
                  : ""}
              </Typography>
            </>
          );
        },
      },
    ],
    [],
  );

  const columnsWithTooltips = useMemo(
    () =>
      columns.map((col) => ({
        ...col,
        renderHeader: (params: GridColumnHeaderParams) => (
          <Tooltip title={params.colDef.headerName} placement="top" arrow>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "13px",
              }}
            >
              {params.colDef.headerName}
            </Typography>
          </Tooltip>
        ),
      })),
    [columns],
  );

  const handleExport = () => {
    setExportLoading(true);
    const exportRequest = {
      fromDate: postModel.filterModel?.fromDate
        ? dayjs(postModel.filterModel.fromDate).format("YYYY-MM-DD")
        : null,
      toDate: postModel.filterModel?.toDate
        ? dayjs(postModel.filterModel.toDate).format("YYYY-MM-DD")
        : null,
    };
    api.downloadExcel(
      "api/ExcelDownload/loginHistoryExports",
      "LoginHistoryExports",
      exportRequest,
      setExportLoading,
    );
  };

  return (
    <>
      <Grid container direction={"row"} spacing={2} sx={{ my: 1 }}>
        <Grid item md={12}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                Recent Logged In Records
              </Typography>
            </Grid>
            <Grid item>
              <LoadingButton
                variant="contained"
                loading={exportLoading}
                size="medium"
                color="primary"
                onClick={handleExport}
                startIcon={<DownloadOutlined />}
              >
                Export
              </LoadingButton>
            </Grid>
          </Grid>

          <Grid container sx={{ height: 450, width: "100%" }}>
            <StyledDataGrid
              getRowId={(row) => row.id}
              rows={data ? data?.apiData?.data : []}
              columns={columnsWithTooltips}
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "wrap",
                  fontWeight: 600,
                  lineHeight: "normal",
                  fontSize: "13px",
                },
                "& .MuiDataGrid-cell": {
                  whitespace: "wrap",
                  fontSize: 14,
                },
              }}
              density="compact"
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25, 50, 100]}
              paginationMode="server"
              sortingMode="server"
              rowCount={data?.apiData?.count}
              initialState={{
                pagination: {
                  paginationModel: { ...postModel.pagingModel },
                },
              }}
              sortModel={postModel.sortModel}
              onSortModelChange={handleSorting}
              onPaginationModelChange={handlePagination}
              disableColumnFilter
              loading={isFetching}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default LoginHistoryTable;
